/**
 * The patient's report.
 *
 * This is the deliverable. The kiosk measures, syncs and sends a link; what
 * arrives at the other end of that link is the only thing most patients — and
 * a fair number of referring clinicians — will ever see of this system.
 *
 * It is laid out as a clinical document, not a product screen: letterhead,
 * identification block, numbered findings sections with a reference range
 * beside every reading, then impression, recommendations and a signature. A
 * patient handing this to a physiotherapist is handing over a document, and it
 * has to survive being read by someone who has never seen this software.
 *
 * PRINT IS NOT AN AFTERTHOUGHT. "Print / Save PDF" is the browser's own dialog,
 * so this layout IS the printed layout. Tables avoid splitting rows, headings
 * stay with what they introduce, figures are capped so a photograph cannot push
 * a page's content off the sheet, and the interface chrome is dropped.
 *
 * Sections appear only when there is data for them: a scan with no pressure mat
 * prints no pressure heading rather than an empty one.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { db } from '../config/firebase';

import { rangeForAngle, severityForAngle } from '../lib/metricBands';
import {
  HEEL_CUTS, HEEL_RANGE, SHIN_CUTS, SHIN_RANGE,
  archTypeProvenance, balanceTestOf, collectFindings, footTypeProvenance, visionReadingOf, formatScanDate, generationNotes,
  hasFindings, heelSide, isUnmeasured, legPosesOf, measuredCount, pairAngles, patientNameOf, postureViewsOf,
  plantarImageryOf, shinSide, worseOfPair,
} from '../lib/report/scanModel';
import { limitScanToSections } from '../lib/report/reportSections';
import { referenceLabel, referenceText, toleranceLabel } from '../lib/report/reference';
import { footComparisonOf } from '../lib/report/footComparison';
import { FOOT_COMPARISON_CSS, FootComparison } from '../components/report/FootComparison';
import {
  KINETIC_CHAIN, formatReference, hasRegionReadings, regionsFor, rowDirections, rowSeverity,
} from '../lib/report/postureRegions';
import { Prose } from '../components/report/Prose';
import { cameraFootSizeCaption, cameraFootSizeRows, staticPlantarRows, walkRows, walkTestOf } from '../lib/report/plantarModel';
import { cameraForView, postureFigure, rearfootFigure } from '../lib/report/body3dModel';
import { drawer3dOf } from '../lib/report/drawer3d';
import { FIG3D_CSS, FigureWith3D } from '../components/report/FigureWith3D';
import { FindingsDrawer, K3D_CSS, KQ3DHandle, canOfferFindings3d, useFindingsDrawer } from '../components/report/FindingsDrawer';
import { settleImages } from '../lib/report/settleImages';
import {
  AngleScales, Authentication, BilateralScale, CLINICAL_CSS, Callout, DataTable, Figure, Grade,
  IdentTable, KeyValues, Letterhead, LoadShareScale, OpticalScales, Reading, Section,
} from '../components/report/clinical';
import { WEB_SKIN_CSS } from '../components/report/webSkin';
import { KINETIQ_LOGO } from '../components/report/brandAsset';
import { ReportLoader } from '../components/report/ReportLoader';

/** Section titles for the written narrative, by what the session examined. */
const NARRATIVE = {
  full: {
    pageFirst: 'Clinical overview',
    pageSecond: 'Biomechanical analysis',
    pageThird: 'Clinical impact and risk',
    pageFourth: 'Orthotic prescription',
    pageFifth: 'Lifestyle and rehabilitation',
  },
  'body-alignment': {
    pageFirst: 'Clinical overview',
    pageSecond: 'Postural analysis',
    pageThird: 'Clinical impact and risk',
    pageFourth: 'Corrective exercise programme',
    pageFifth: 'Lifestyle, ergonomics and follow-up',
  },
};

const VIEW_NOTE = {
  front: 'Anterior view, relaxed bilateral stance.',
  back: 'Posterior view, relaxed bilateral stance.',
  side: 'Lateral view, relaxed bilateral stance.',
};

const POSE_NOTE = {
  stand: 'Posterior view, relaxed calcaneal stance.',
  'knee-bend': 'Posterior view, knees flexed under load.',
  'toe-up': 'Posterior view, heels raised.',
};

function Loading() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f3f5' }}>
      <ReportLoader label="Loading report" />
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f3f5' }}>
      <div style={{ background: '#fff', border: '1px solid #d8dbdf', padding: '32px 36px', maxWidth: 420, textAlign: 'center', font: '13px/1.5 Helvetica, Arial, sans-serif' }}>
        <p style={{ fontWeight: 700, fontSize: 15, margin: '0 0 8px' }}>Report not available</p>
        <p style={{ color: '#4a5560', margin: 0 }}>
          This link may have expired, or the report may have been removed. Your clinic can issue a new one.
        </p>
      </div>
    </div>
  );
}

/** One measured angle as a table cell, coloured by its own grade. */
function cell(angle) {
  if (!angle || typeof angle.degrees !== 'number') return '—';
  return <Reading degrees={angle.degrees} severity={severityForAngle(angle)} />;
}

/** The reference cell: 0° with its tolerance under it, or the range (lib/report/reference). */
function refOf(angle) {
  const r = angle && rangeForAngle(angle);
  const tol = toleranceLabel(r);
  return (
    <>
      {referenceLabel(r)}
      {tol && <span style={{ display: 'block', fontSize: '0.8em', color: '#5b6670', fontWeight: 400 }}>{tol}</span>}
    </>
  );
}

const SEVERITY_NAME = { normal: 'Normal', mild: 'Mild', moderate: 'Moderate', severe: 'Severe' };

/** One side's reading on the comparison page: its value, or why there is none. */
function sideCell(angle) {
  if (!angle) return '—';
  return Number.isFinite(angle.degrees) ? cell(angle) : 'Not measured';
}

/** One side's grade as a badge, for the comparison page. */
function gradeBadge(angle, side) {
  if (!angle || !Number.isFinite(angle.degrees)) return null;
  const severity = angle.severity || rangeForAngle(angle) ? severityForAngle(angle) : null;
  if (!severity) return <span>{side ? `${side}: ` : ''}Not classified</span>;
  return (
    <span className={`severity-badge ${severity}`} style={{ marginRight: 4 }}>
      {side ? `${side} · ` : ''}{SEVERITY_NAME[severity] || 'Not classified'}
    </span>
  );
}

/**
 * Grade the left/right load split against the 50 ± 5 % reference.
 *
 * This cell used to be hard-coded normal, which would have printed "within
 * normal limits" beside a 62/38 split. The reference is stated in the row
 * beside it, so the grade has to be derived from the same numbers a reader
 * can check.
 */
function loadShareSeverity(bilateral) {
  const right = bilateral?.right;
  const left = bilateral?.left;
  if (typeof right !== 'number' && typeof left !== 'number') return null;
  const off = Math.max(
    typeof right === 'number' ? Math.abs(right - 50) : 0,
    typeof left === 'number' ? Math.abs(left - 50) : 0,
  );
  if (off <= 5) return 'normal';
  if (off <= 10) return 'mild';
  if (off <= 15) return 'moderate';
  return 'severe';
}

/**
 * Does this view carry anything for the measurement tables? A view without
 * angles — a tracking frame, say — would otherwise leave half the row blank
 * beside its figure, which reads as a missing table rather than as a view
 * that has none.
 */
function hasAngles(view) {
  const { bilateral, midline } = pairAngles(view.angles);
  return bilateral.length > 0 || midline.length > 0;
}

/** Bilateral and midline measurement tables for one analysed view. */
function ViewTables({ view, n }) {
  const { bilateral, midline } = pairAngles(view.angles);
  return (
    <>
      {bilateral.length > 0 && (
        <DataTable
          caption={`Table ${n}a — ${view.label}, bilateral measurements`}
          head={['Measurement', 'Left', 'Right', 'Reference', 'Finding']}
          numeric={[1, 2, 3]}
          rows={bilateral.map(row => {
            const worse = worseOfPair(row.right, row.left);
            return [
              row.label,
              cell(row.left),
              cell(row.right),
              refOf(row.left ?? row.right),
              <Grade severity={worse ? severityForAngle(worse) : 'normal'} />,
            ];
          })}
        />
      )}
      {midline.length > 0 && (
        <DataTable
          caption={`Table ${n}${bilateral.length ? 'b' : 'a'} — ${view.label}, midline measurements`}
          head={['Measurement', 'Value', 'Reference', 'Finding']}
          numeric={[1, 2]}
          rows={midline.map(a => [a.name, cell(a), refOf(a), <Grade severity={severityForAngle(a)} />])}
        />
      )}
    </>
  );
}

/** One region row's readings, each coloured by its own grade, with the level differences beside. */
function regionReading(row) {
  const parts = [];
  row.readings.forEach(({ angle }, i) => {
    const side = /^(Left|Right)\b/.exec(angle.name ?? '')?.[1];
    parts.push(
      <React.Fragment key={`a${i}`}>
        {side ? `${side} ` : ''}<Reading degrees={angle.degrees} severity={severityForAngle(angle)} />
      </React.Fragment>,
    );
  });
  row.offsets.forEach(({ offset }, i) => parts.push(<React.Fragment key={`o${i}`}>{offset.cm.toFixed(1)} cm (est.)</React.Fragment>));
  return parts.flatMap((p, i) => (i ? [' · ', p] : [p]));
}

/**
 * The six-region findings page: for each region a table of what was measured,
 * with the direction of every out-of-range reading, what could not be measured
 * said plainly, and the written section beneath it.
 */
function RegionsSection({ views, analysis, crux, n }) {
  const regions = regionsFor(views);
  if (!hasRegionReadings(regions)) return null;
  return (
    <section className="report-page regions-page" key="regions" data-testid="regions-page">
      <p className="eyebrow">POSTURAL FINDINGS BY REGION</p>
      <Section title={`Findings ${n} — Body regions`} sub="Head to foot" />
      <p className="note">Every region is listed. A measurement that could not be taken is shown as not measured — it is never treated as normal.</p>
      {regions.map((region, i) => (
        <div className="region" key={region.id} data-testid={`region-${region.id}`}>
          <h3 className="sub">{i + 1}. {region.title}</h3>
          <DataTable
            head={['Measurement', 'Reading', 'Reference', 'Finding']}
            numeric={[1, 2]}
            rows={region.rows.map(row => {
              if (!row.measured) return [row.label, 'Not measured', '—', row.unmeasurable ?? 'Not measured'];
              const dirs = rowDirections(row);
              return [
                row.label,
                regionReading(row),
                row.readings.length ? formatReference(row) : '—',
                <><Grade severity={rowSeverity(row)} />{dirs.length > 0 && ` — ${dirs.join('; ')}`}</>,
              ];
            })}
          />
          {analysis?.regions?.[region.id] && <Prose text={analysis.regions[region.id]} crux={crux} />}
        </div>
      ))}
      {analysis?.chain && (
        <>
          <h3 className="sub">{KINETIC_CHAIN.join(' → ').toUpperCase()}</h3>
          <Callout label="From head to foot" tone="info"><Prose text={analysis.chain} crux={crux} /></Callout>
        </>
      )}
    </section>
  );
}

/**
 * "Print / Save PDF", which only prints a COMPLETE report.
 *
 * The browser's print dialog captures whatever has painted at that moment. On a
 * phone the report's photographs were still arriving, so the PDF had blank
 * white figures and half-drawn ones. The images start loading as soon as the
 * report opens; the button says how many are in, and prints only when every one
 * has loaded and decoded. An image that fails is retried; if some still will
 * not load the button offers to try again rather than printing a report with
 * holes in it.
 */
function PrintButton({ reloadKey }) {
  const [state, setState] = useState({ phase: 'loading', loaded: 0, total: 0, failed: 0 });
  const running = React.useRef(false);

  const load = React.useCallback(async () => {
    if (running.current) return null;
    running.current = true;
    const images = Array.from(document.querySelectorAll('.clinical img'));
    setState(s => ({ ...s, phase: 'loading', loaded: 0, total: images.length, failed: 0 }));
    const result = await settleImages(images, {
      onProgress: (loaded, total) => setState(s => ({ ...s, loaded, total })),
    });
    running.current = false;
    setState({ phase: result.failed ? 'failed' : 'ready', loaded: result.loaded, total: result.total, failed: result.failed });
    return result;
  }, []);

  useEffect(() => { void load(); }, [load, reloadKey]);

  const onClick = async () => {
    if (state.phase === 'loading') return;
    if (state.phase === 'failed') { await load(); return; }
    // Images can have been dropped by the browser since (memory pressure on a
    // phone): confirm they are all still there immediately before printing.
    const images = Array.from(document.querySelectorAll('.clinical img'));
    if (images.some(img => !(img.complete && img.naturalWidth > 0))) {
      const result = await load();
      if (!result || result.failed) return;
    }
    window.print();
  };

  const label = {
    loading: `Preparing images… ${state.loaded} / ${state.total}`,
    ready: 'Print / Save PDF',
    failed: `${state.failed} image${state.failed === 1 ? '' : 's'} did not load — tap to retry`,
  }[state.phase];

  return (
    <button
      type="button"
      data-testid="print-report"
      data-phase={state.phase}
      disabled={state.phase === 'loading'}
      onClick={onClick}
      style={{
        marginLeft: 'auto', background: state.phase === 'failed' ? '#b42318' : '#14181c', color: '#fff', border: 0,
        padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: state.phase === 'loading' ? 'wait' : 'pointer',
        opacity: state.phase === 'loading' ? 0.75 : 1,
      }}
    >
      {label}
    </button>
  );
}

/**
 * The report itself, given a scan document.
 *
 * Separated from the page that fetches it so the layout can be rendered
 * anywhere a scan-shaped object exists — a test, a preview, a future
 * clinic-side view — without a Firestore connection in the way.
 */
export function ReportDocument({ scan: storedScan, id }) {
  // Only the scan sections the clinic was given (lib/report/reportSections).
  const scan = useMemo(() => limitScanToSections(storedScan), [storedScan]);
  const model = useMemo(() => {
    if (!scan) return null;
    const views = postureViewsOf(scan);
    const poses = legPosesOf(scan);
    const findings = collectFindings(views, poses);
    return {
      views, poses, findings,
      measured: measuredCount(views, poses),
      ...plantarImageryOf(scan),
      balance: balanceTestOf(scan),
      walk: walkTestOf(scan),
      staticTable: staticPlantarRows(scan.sensorAnalytics),
      footSizeRows: cameraFootSizeRows(scan.footSize, scan.sensorAnalytics),
      glance: footComparisonOf(scan),
      notes: generationNotes(scan),
      name: patientNameOf(scan),
      scanDate: formatScanDate(scan.timestamp),
      // The optional 3D view behind the posture and rearfoot figures.
      figure3d: { posture: postureFigure(views), rearfoot: rearfootFigure(poses) },
      // Everything the 3D findings drawer shows.
      drawer3d: drawer3dOf(scan, views, poses),
    };
  }, [scan]);
  const drawer = useFindingsDrawer();

  if (!scan || !model) return null;

  const {
    vendorName, medialArchType, footType, archVisionAnalysis, visionAnalysis,
    analysis, captureQuality, sensorAnalytics, archVitals, problem, activities,
    conditions, reportKind, occupation, lifestyle, height, weight, age, gender, email, phone,
  } = scan;

  // The plantar and toe up photographs, and the pressure maps, as the operator chose (plantarImageryOf).
  const { views, poses, findings, measured, pressure, footPhotos, balance, walk, staticTable, footSizeRows, notes, name, scanDate, figure3d, drawer3d } = model;
  // Named by capture; a photo with no sole on the glass has nothing to show.
  const opticalVitals = (Array.isArray(archVitals) ? archVitals : [])
    .map((v, i) => ({ v, view: i === 0 ? 'Plantar' : 'Toe up' }))
    .filter(({ v }) => v && v.contactFound !== false);
  const offer3d = canOfferFindings3d(drawer3d);
  const titles = NARRATIVE[reportKind === 'body-alignment' ? 'body-alignment' : 'full'];
  const bodyOnly = reportKind === 'body-alignment';
  const stand = poses.find(p => p.pose === 'stand') ?? poses[0] ?? null;

  // Figures are numbered across the whole document, in the order they appear.
  let figureNo = 0;
  const nextFigure = () => ++figureNo;

  // Findings sections are numbered by what is actually present.
  let sectionNo = 0;
  const nextSection = () => ++sectionNo;

  // The readings the summary table flagged, handed to the prose so the lines
  // that discuss them can be marked. Values only — the prose is never asked to
  // agree with anything but the numbers already printed above it.
  const crux = findings.map(f => f.degrees);

  // Every view's measurements as left/right pairs and midline singles, for the comparison page.
  const comparison = [...views, ...poses].map(view => ({ view, ...pairAngles(view.angles) }));

  const bmi = height && weight
    ? (Number(weight) / Math.pow(Number(height) / 100, 2)).toFixed(1)
    : null;

  return (
    <>
      <style>{CLINICAL_CSS}</style>
      <style>{WEB_SKIN_CSS}</style>
      <style>{FIG3D_CSS}</style>
      <style>{FOOT_COMPARISON_CSS}</style>
      <style>{K3D_CSS}</style>

      <header
        className="no-print"
        style={{
          position: 'sticky', top: 0, zIndex: 40, background: '#fff',
          borderBottom: '1px solid #d8dbdf', padding: '8px 16px',
          display: 'flex', alignItems: 'center', gap: 12,
          font: '12px Helvetica, Arial, sans-serif',
        }}
      >
        <span style={{ fontWeight: 700 }}>{name}</span>
        <span style={{ color: '#5b6670' }}>{scanDate}</span>
        <PrintButton reloadKey={id} />
        {offer3d && <KQ3DHandle drawer={drawer} variant="bar" />}
      </header>
      {offer3d && <FindingsDrawer drawer={drawer} data={drawer3d} title={name} />}

      <div
        className="clinical modern-report"
        style={{ minHeight: '100vh', '--wm': `url(${KINETIQ_LOGO})` }}
      >
        <div className="watermark" aria-hidden="true" />
        <div className="sheet">

          <section className="report-page report-cover" aria-label="Clinical report overview">
          <Letterhead clinic={vendorName} logo={KINETIQ_LOGO} action={offer3d ? <KQ3DHandle drawer={drawer} variant="letterhead" /> : null} />
          <p className="eyebrow">PERSONALISED CLINICAL REPORT</p>

          <h1 className="doctitle">KinetiQ Biomechanical Assessment</h1>

          <IdentTable
            rows={[
              ['User', name, 'Report no.', id],
              ['Age / Sex', [age && `${age} y`, gender].filter(Boolean).join(' / ') || '—', 'Date of assessment', scanDate],
              ['Height / Weight',
                [height && `${height} cm`, weight && `${weight} kg`].filter(Boolean).join(' / ') +
                (bmi ? ` (BMI ${bmi})` : '') || '—',
                'Reporting clinic', vendorName || 'KinetiQ Footcare'],
              ['Occupation', occupation, 'Contact', email || phone],
            ]}
          />

          <div className="overview-grid">
            <div className="overview-card"><span>Recorded measurements</span><strong>{measured}</strong><small>Across the captured views</small></div>
            <div className="overview-card attention"><span>Outside reference range</span><strong>{findings.length}</strong><small>See actual angles on the next page</small></div>
            {!bodyOnly && <div className="overview-card"><span>Foot classification</span><strong>{footType || archVisionAnalysis?.pronationStatus || 'Not recorded'}</strong><small>Medial arch: {medialArchType || archVisionAnalysis?.archHeight || 'Not recorded'}</small></div>}
          </div>
          {/* ── Clinical history ───────────────────────────────────────── */}
          <Section title="Clinical history" />
          {problem
            ? <p>{problem}</p>
            : <p>No presenting complaint was recorded at intake.</p>}
          <KeyValues
            rows={[
              ['Reported conditions', Array.isArray(conditions) && conditions.length ? conditions.join(', ') : 'None reported'],
              ['Activity', Array.isArray(activities) && activities.length ? activities.join(', ') : null],
              ['Lifestyle', lifestyle],
            ]}
          />

          <div className="cover-summary">
            <b>Assessment overview</b>
            <p>{measured ? `${measured} recorded measurements · ${findings.length} outside reference range` : 'No measurements recorded'}</p>
            <p>Actual angles, reference ranges and severity are listed on the following page.</p>
          </div>
          </section>
          {model.glance.length > 0 && (
            <section className="report-page glance-page" aria-label="Left and right at a glance">
              <Section title="Left and right at a glance" sub="Each measurement with the left foot on the left and the right foot on the right" />
              <p className="note">The middle column names what is measured, its unit and its normal range. The grade under each value is that foot's own; a dash is a measurement this scan did not take.</p>
              <FootComparison rows={model.glance} />
            </section>
          )}
          <section className="report-page measurement-summary page-break" aria-label="Actual degrees and reference ranges">
            <Section title="Measurement comparison" sub="Left and right side by side · actual degrees vs reference" />
            <p className="note">Each joint is shown once, with the left and right side beside each other and the difference between them. The reference is 0° (neutral) wherever neutral is normal, with how far from it is still normal beneath; each side is graded on its own. Missing values are not classified as normal.</p>
            <div className="severity-legend"><span>Normal</span><span>Mild</span><span>Moderate</span><span>Severe</span></div>
            <div className="measurement-scroll">
            <DataTable
              caption="Left and right"
              head={['Measurement', 'View / position', 'Left', 'Right', 'L–R difference', 'Reference', 'Grade']}
              numeric={[2, 3, 4, 5]}
              rows={comparison.flatMap(({ view, bilateral }) => bilateral.map(row => {
                const both = Number.isFinite(row.left?.degrees) && Number.isFinite(row.right?.degrees);
                return [
                  row.label, view.label,
                  sideCell(row.left), sideCell(row.right),
                  both ? `${Math.abs(row.left.degrees - row.right.degrees).toFixed(1)}°` : '—',
                  refOf(row.left ?? row.right),
                  <>{gradeBadge(row.left, 'L')}{gradeBadge(row.right, 'R')}</>,
                ];
              }))} />
            <DataTable
              caption="Midline"
              head={['Measurement', 'View / position', 'Actual angle', 'Reference', 'Grade']}
              numeric={[2, 3]}
              rows={comparison.flatMap(({ view, midline }) => midline.map(angle => [
                angle.name, view.label, sideCell(angle), refOf(angle),
                gradeBadge(angle) ?? 'Not measured',
              ]))} />
            </div>
            {measured === 0 && <p>No angle measurements were recorded for this assessment.</p>}
          </section>

          {/* ── Findings: static posture ───────────────────────────────── */}
          {views.length > 0 && (() => {
            const n = nextSection();
            return (
              <section className="report-page posture-page" key="posture"><p className="eyebrow">STATIC POSTURE</p>
                <Section title={`Findings ${n} — Static posture`} sub={views.map(v => v.label).join(' · ')} />
                <div className="view-grid">{views.map((v, i) => (
                  <article className="view-card" key={v.view ?? i}>
                    <h3 className="sub">{v.label}</h3>
                    <div className="view-detail">
                      <FigureWith3D figure={v.overlayUrl ? figure3d.posture : null} kind="posture" camera={cameraForView(v.view)} view={v.label}>
                        <Figure
                          url={v.overlayUrl}
                          num={nextFigure()}
                          title="Skeleton overlay"
                          view={v.label}
                          caption={`${v.label}. ${VIEW_NOTE[v.view] ?? ''}`}
                          notes={[
                            v.landmarksSeeded && v.landmarksEdited && 'The detector could not read this frame; the landmarks were placed by the clinician. Read these values as a clinician’s assessment.',
                            !v.landmarksSeeded && v.landmarksEdited && 'Landmark positions were reviewed and adjusted by the clinician.',
                            !v.landmarksSeeded && !v.landmarksEdited && 'Landmarks were detected automatically (AI) and were not adjusted by the clinician.',
                          ]}
                        />
                      </FigureWith3D>
                      {hasAngles(v) && <div className="view-measurements"><ViewTables view={v} n={`${n}.${i + 1}`} /><AngleScales angles={v.angles} /></div>}
                    </div>
                    {v.summary && (
                      <Callout label={`${v.label} — assessment`}>
                        <Prose text={v.summary} />
                      </Callout>
                    )}
                    {Array.isArray(v.deviations) && v.deviations.length > 0 && (
                      <ul className="bul">{v.deviations.map((d, di) => <li key={di}>{d}</li>)}</ul>
                    )}
                  </article>
                ))}</div>
              </section>
            );
          })()}

          {/* ── Findings: by body region ───────────────────────────────── */}
          {views.length > 0 && hasRegionReadings(regionsFor(views)) && (
            <RegionsSection views={views} analysis={analysis} crux={crux} n={nextSection()} />
          )}

          {/* ── Findings: rearfoot and lower limb ──────────────────────── */}
          {poses.length > 0 && (() => {
            const n = nextSection();
            return (
              <section className="report-page limb-page" key="limb"><p className="eyebrow">REARFOOT AND LOWER LIMB</p>
                <Section title={`Findings ${n} — Rearfoot and lower limb`} sub="Relaxed calcaneal stance" />
                <div className="pose-grid">{poses.map((p, i) => (
                  <article className="view-card" key={p.pose ?? i}>
                    <h3 className="sub">{p.label}</h3>
                    {isUnmeasured(p) ? (
                      // Placeholder markers are not a measurement. The image
                      // would show dots at fixed positions over the patient's
                      // legs, so it is not shown at all.
                      <div className="unmeasured" data-testid={`unmeasured-${p.pose ?? i}`}>
                        <strong>Not measured.</strong>{' '}
                        The joints could not be detected automatically in this capture and the markers were not
                        placed by the clinician, so no rearfoot or lower-limb reading is reported for it.
                      </div>
                    ) : (
                    <div className="view-detail">
                      <FigureWith3D figure={p === stand && p.overlayUrl ? figure3d.rearfoot : null} kind="rearfoot" view={p.label}>
                        <Figure
                          url={p.overlayUrl}
                          num={nextFigure()}
                          title="Rearfoot axes"
                          view={p.label}
                          caption={`${p.label}. ${POSE_NOTE[p.pose] ?? ''} Tibial and calcaneal axes with the malleolar line across each ankle.`}
                          notes={[
                            p.landmarksSeeded && p.landmarksEdited && 'The detector could not read this frame; the landmarks were placed by the clinician. Read these values as a clinician’s assessment.',
                            !p.landmarksSeeded && p.landmarksEdited && 'Landmark positions were reviewed and adjusted by the clinician.',
                            !p.landmarksSeeded && !p.landmarksEdited && 'Landmarks were detected automatically (AI) and were not adjusted by the clinician.',
                          ]}
                        />
                      </FigureWith3D>
                      <div className="capture-metrics">{(p.angles ?? []).map((a, index) => <p key={index}><span>{a.name}</span><strong>{cell(a)}</strong></p>)}</div>
                    </div>
                    )}
                    {/* The capture's own written assessment. Rewritten whenever
                        its markers are moved, so it always describes the angles
                        printed above it. */}
                    {p.summary && !isUnmeasured(p) && (
                      <Callout label="Assessment">
                        <Prose text={p.summary} />
                      </Callout>
                    )}
                  </article>
                ))}</div>

                {stand && hasFindings(stand) && (
                  <>
                    <h3 className="sub">Direction and magnitude</h3>
                    <BilateralScale
                      title="Heel — rearfoot tilt"
                      range={HEEL_RANGE} cuts={HEEL_CUTS}
                      leftLabel="Inversion" rightLabel="Eversion"
                      left={heelSide(stand.findings.heelLeft)}
                      right={heelSide(stand.findings.heelRight)}
                    />
                    <BilateralScale
                      title="Tibial — lower-leg rotation"
                      range={SHIN_RANGE} cuts={SHIN_CUTS}
                      leftLabel="Tibial torsion" rightLabel="Fibular torsion"
                      left={shinSide(stand.findings.shinLeft)}
                      right={shinSide(stand.findings.shinRight)}
                    />
                    <p className="note">
                      Magnitude is measured. The direction each finding is named under is confirmed
                      by the assessing clinician where it is marked as such.
                    </p>
                  </>
                )}
              </section>
            );
          })()}

          {(pressure.length > 0 || footPhotos.length > 0 || sensorAnalytics || footSizeRows || (!bodyOnly && (medialArchType || footType || archVisionAnalysis))) && <section className="report-page plantar-page">
          {/* ── Findings: plantar pressure ─────────────────────────────── */}
          {(pressure.length > 0 || footPhotos.length > 0 || sensorAnalytics || footSizeRows) && (() => {
            const n = nextSection();
            return (
              <section className="pressure-page" key="pressure"><p className="eyebrow">PLANTAR CONTACT</p>
                {pressure.length > 0 || footPhotos.length > 0 || sensorAnalytics
                  ? <Section title={`Findings ${n} — Plantar pressure`} sub="Static bilateral stance" />
                  : <Section title={`Findings ${n} — Foot size`} sub="Measured on the podoscope glass" />}
                {pressure.length > 0 && (
                  <div className={`figrow ${pressure.length >= 3 ? 'three' : 'two'}`}>
                    {pressure.map((m, i) => (
                      <Figure
                        key={i} url={m.url} num={nextFigure()}
                        title={m.source === 'sensor' ? 'Pressure map' : 'Optical estimate'}
                        view={m.label}
                        caption={`${m.label}.`}
                        notes={[m.source === 'sensor'
                          ? 'Measured on the sensor platform.'
                          : 'Optical estimate derived from the podoscope photograph, not a measurement.']}
                      />
                    ))}
                  </div>
                )}
                {/* The feet themselves, beside their maps (the kiosk uploads them from November 2026). */}
                {footPhotos.length > 0 && (
                  <div className="figrow two" data-testid="foot-photos">
                    {footPhotos.map(({ url, index: i }) => {
                      const view = i === 0 ? 'Plantar' : 'Toe up';
                      // Cut out on the kiosk (podoscope/footCutout): the feet only, stood upright, on white.
                      const cutout = scan.footPhotoKinds?.[i] === 'cutout';
                      return (
                        <Figure
                          key={url} url={url} num={nextFigure()}
                          title={cutout ? 'Foot outline' : 'Photograph'} view={`${view} view`}
                          caption={cutout
                            ? `${view} view — each foot cut out of the podoscope photograph.`
                            : `${view} view, as photographed on the podoscope.`}
                          notes={[cutout
                            ? 'Left foot on the left, marked L and R; the dotted box spans its length, the blue lines its ball and heel widths.'
                            : scan.footPhotosMirrored
                              ? 'Shown with the left foot on the left, as the pressure maps, and marked L and R.'
                              : 'As the camera sees it from below the glass: the left foot is on the right.']}
                        />
                      );
                    })}
                  </div>
                )}
                {staticTable && (
                  <>
                  {/* Laid out as the reference report: left foot, parameter, right foot. */}
                  <DataTable
                    caption={staticTable.perFoot
                      ? 'Static test — each foot measured on the sensor platform'
                      : 'Static test — whole-platform figures (this scan predates per-foot readings)'}
                    head={['Left', 'Parameter', 'Right', 'Reference']}
                    numeric={[0, 2]}
                    rows={staticTable.rows.map(r => [r.left, r.label, r.right, r.reference ?? '—'])}
                  />
                  <LoadShareScale bilateral={sensorAnalytics.bilateral} />
                  </>
                )}
                {footSizeRows && (
                  <DataTable
                    caption={cameraFootSizeCaption(scan.footSize)}
                    head={['Left', 'Parameter', 'Right', 'Reference']}
                    numeric={[0, 2]}
                    rows={footSizeRows.map(r => [r.left, r.label, r.right, r.reference ?? '—'])}
                  />
                )}
                {opticalVitals.length > 0 && (
                  <>
                  <DataTable
                    caption="Optical arch vitals, derived from the podoscope frames"
                    head={['View', 'Medial bias', 'High pressure', 'Load']}
                    numeric={[1, 2, 3]}
                    rows={opticalVitals.map(({ v, view }) => [view, v.medialBias, v.highPressure, v.load])}
                  />
                  <OpticalScales rows={opticalVitals.flatMap(({ v, view }) => [
                    { label: `${view} — medial bias`, value: v.medialBias },
                    { label: `${view} — high-contact area`, value: v.highPressure },
                    { label: `${view} — optical load estimate`, value: v.load },
                  ])} />
                  </>
                )}
              </section>
            );
          })()}

          {/* ── Findings: eyes-closed balance ──────────────────────────── */}
          {balance && (() => {
            const n = nextSection();
            const m = balance.metrics;
            const missing = balance.expectedShots - balance.shots.length;
            return (
              <React.Fragment key="balance">
                <Section
                  title={`Findings ${n} — Balance, eyes closed`}
                  sub="Centre-of-pressure sway with vision removed"
                />
                {m ? (
                  // No reference column and no grade: the clinic has supplied no
                  // normal ranges for sway, and a band invented here would be
                  // indistinguishable on the page from the validated ones above.
                  <DataTable
                    caption="Postural sway over the hold — no reference range is published for these parameters"
                    head={['Parameter', 'Value', 'Unit']}
                    numeric={[1]}
                    rows={[
                      ['Sway velocity', m.velocityMmPerS, 'mm/s'],
                      ['Path length', m.pathLengthMm, 'mm'],
                      ['Mediolateral range', m.mlRangeMm, 'mm'],
                      ['Anteroposterior range', m.apRangeMm, 'mm'],
                      ['95% confidence ellipse area', m.ellipseAreaMm2, 'mm²'],
                      ['Mean radius', m.meanRadiusMm, 'mm'],
                      ['Hold duration', m.durationMs != null ? (m.durationMs / 1000).toFixed(1) : '—', 's'],
                      ['Samples recorded', m.sampleCount, ''],
                    ]}
                  />
                ) : (
                  <p>
                    The sway could not be measured from this hold
                    {balance.inconclusive ? `: ${balance.inconclusive}` : '.'}
                  </p>
                )}
                {balance.traceUrl && (
                  <Figure
                    url={balance.traceUrl} num={nextFigure()}
                    caption="Centre-of-pressure trace over the hold."
                    notes={['Recorded on the sensor platform with the eyes closed.']}
                  />
                )}
                {balance.shots.length > 0 && (
                  <div className={`figrow ${balance.shots.length >= 3 ? 'three' : 'two'}`}>
                    {balance.shots.map((url, i) => (
                      <Figure
                        key={i} url={url} num={nextFigure()}
                        caption={`Stance during the eyes-closed hold, frame ${i + 1}.`}
                      />
                    ))}
                  </div>
                )}
                {missing > 0 && (
                  <p>
                    {missing} of the {balance.expectedShots} frames captured during this hold could not
                    be stored. The measurement above is unaffected: it is recorded from the platform
                    before the images are uploaded.
                  </p>
                )}
              </React.Fragment>
            );
          })()}

          {/* ── Findings: walking (dynamic) test ───────────────────────── */}
          {walk && (() => {
            const n = nextSection();
            const note = 'Peak pressure through stance, with the centre-of-pressure path (coloured), the foot axis '
              + '(black) and the line of walking (dashed) the foot progression angle is measured against.';
            return (
              <section className="walk-page" key="walk" data-testid="walk-section"><p className="eyebrow">DYNAMIC TEST</p>
                <Section
                  title={`Findings ${n} — Walking (dynamic) test`}
                  sub={`${walk.stepCount} footstep${walk.stepCount === 1 ? '' : 's'} recorded walking across the sensor platform`}
                />
                {walk.inconclusive ? (
                  <p>The walking pattern could not be measured: {walk.inconclusive}</p>
                ) : (
                  <>
                    {(walk.images.leftPhases || walk.images.rightPhases) && (
                      <div className="figrow two">
                        <Figure url={walk.images.leftPhases} num={walk.images.leftPhases ? nextFigure() : 0}
                          title="Left foot" view="Dynamic pressure"
                          caption="Left foot through stance: heel strike, mid-stance and toe-off." />
                        <Figure url={walk.images.rightPhases} num={walk.images.rightPhases ? nextFigure() : 0}
                          title="Right foot" view="Dynamic pressure"
                          caption="Right foot through stance: heel strike, mid-stance and toe-off." />
                      </div>
                    )}
                    {(walk.images.leftFootprint || walk.images.rightFootprint) && (
                      <div className="figrow two">
                        <Figure url={walk.images.leftFootprint} num={walk.images.leftFootprint ? nextFigure() : 0}
                          title="Centre of pressure trajectory" view="Left foot" caption={`Left foot. ${note}`} />
                        <Figure url={walk.images.rightFootprint} num={walk.images.rightFootprint ? nextFigure() : 0}
                          title="Centre of pressure trajectory" view="Right foot" caption={`Right foot. ${note}`} />
                      </div>
                    )}
                    <DataTable
                      caption="Walking test — each foot"
                      head={['Left', 'Parameter', 'Right', 'Reference']}
                      numeric={[0, 2]}
                      rows={walkRows(walk).map(r => [r.left, r.label, r.right, r.reference ?? '—'])}
                    />
                    {walk.images.curves && (
                      <Figure url={walk.images.curves} num={nextFigure()} title="Gait curves"
                        caption="Plantar contact area and total plantar load through stance, left against right." />
                    )}
                    {walk.observations.length > 0 && (
                      <Callout label="Observations" tone="info">
                        <ul className="bul">{walk.observations.map((o, i) => <li key={i}>{o}</li>)}</ul>
                      </Callout>
                    )}
                    <p className="note">
                      Pressures are relative values from the platform, on the same scale as the static test.
                      Foot progression is measured {walk.progressionSource === 'steps'
                        ? 'against the line of walking through the recorded steps.'
                        : "against the platform's long axis."}
                    </p>
                  </>
                )}
              </section>
            );
          })()}

          {/* ── Findings: foot type ────────────────────────────────────── */}
          {!bodyOnly && (medialArchType || footType || archVisionAnalysis) && (() => {
            const n = nextSection();
            return (
              <section className="foot-type-page" key="foottype"><p className="eyebrow">FOOT CLASSIFICATION</p>
                <Section title={`Findings ${n} — Foot type`} />
                <KeyValues
                  rows={[
                    // The recorded classification first — what the clinician chose or kept.
                    ['Medial arch', medialArchType || archVisionAnalysis?.archHeight],
                    ['Arch type derivation', archTypeProvenance(scan)],
                    ['Functional foot type', footType || archVisionAnalysis?.pronationStatus],
                    ['Derivation', footTypeProvenance(scan) || 'Confirmed by the assessing clinician'],
                    ['AI vision reading', visionReadingOf(scan)],
                  ]}
                />
                {archVisionAnalysis?.clinicalFindings && (
                  <Callout label="Clinical findings — foot scan" tone="info">
                    <Prose text={archVisionAnalysis.clinicalFindings} />
                  </Callout>
                )}
                {(archVisionAnalysis?.leftFoot || archVisionAnalysis?.rightFoot) && (
                  <KeyValues
                    rows={[
                      ['Right foot', archVisionAnalysis.rightFoot],
                      ['Left foot', archVisionAnalysis.leftFoot],
                    ]}
                  />
                )}
              </section>
            );
          })()}

          </section>}
          <section className="report-page interpretation-page"><p className="eyebrow">CLINICAL INTERPRETATION</p>
          {/* ── Summary of abnormal findings ───────────────────────────── */}
          <Section
            title="Summary of findings outside reference range"
            sub={measured ? `${findings.length} of ${measured} measurements` : 'No measurements recorded'}
          />
          {measured === 0 ? (
            <p>
              No measurements were recorded in this session, so there is nothing to report against
              a reference range.
            </p>
          ) : findings.length === 0 ? (
            <p>Every measurement taken in this session fell inside its reference range.</p>
          ) : (
            <DataTable
              head={['Measurement', 'View', 'Value', 'Reference', 'Deviation', 'Grade']}
              numeric={[2, 3, 4]}
              rows={findings.map(f => [
                f.name, f.context,
                <Reading degrees={f.degrees} severity={f.severity} />,
                refOf({ name: f.name, normalRange: f.range }),
                `+${f.excess.toFixed(1)}°`,
                <Grade severity={f.severity} />,
              ])}
            />
          )}
          {notes.map(n => <p key={n.id} className="note">{n.text}</p>)}

          {/* ── Interpretation, impression, recommendations ────────────── */}
          {analysis?.pageSecond && (
            <>
              <Section title="Interpretation" sub={titles.pageSecond} />
              {/* The crux, before the prose that explains it. Built from the
                  findings table rather than from the writing, so it cannot
                  disagree with the measurements above. */}
              {measured > 0 && (
                <Callout
                  label={findings.length ? 'What to look at first' : 'Summary'}
                  tone={findings.length === 0 ? 'good' : 'warn'}
                >
                  {findings.length === 0 ? (
                    <p>Every one of the {measured} measurements taken fell inside its reference range.</p>
                  ) : (
                    <ul className="keypoints">
                      {findings.slice(0, 4).map((f, i) => (
                        <li key={i}>
                          {f.name} ({f.context.toLowerCase()}) —{' '}
                          <b className="key">{f.degrees.toFixed(1)}°</b>
                          {f.range && <> against a reference of {referenceText(f.range)}</>},{' '}
                          <Grade severity={f.severity} />
                        </li>
                      ))}
                      {findings.length > 4 && (
                        <li>{findings.length - 4} further measurement{findings.length - 4 === 1 ? '' : 's'} outside range, listed in the table above.</li>
                      )}
                    </ul>
                  )}
                </Callout>
              )}
              <Prose text={analysis.pageSecond} crux={crux} />
            </>
          )}

          </section>
          {(analysis?.pageFirst || analysis?.pageThird || visionAnalysis?.postureSummary
            || visionAnalysis?.bodyAlignment) && (
            <section className="report-page impression-page"><p className="eyebrow">CLINICAL IMPRESSION</p>
              <Section title="Impression" />
              {analysis?.pageFirst && <Prose text={analysis.pageFirst} crux={crux} />}
              {visionAnalysis?.postureSummary && (
                <Callout label="Posture summary" tone="info">
                  <Prose text={visionAnalysis.postureSummary} />
                </Callout>
              )}
              {/* Recorded by the vision pass and, until now, never printed —
                  the document held a per-segment reading of the patient's
                  alignment and showed the patient none of it. */}
              {visionAnalysis?.bodyAlignment && (
                <Callout label="Body alignment details">
                  <Prose text={visionAnalysis.bodyAlignment} />
                </Callout>
              )}
              {analysis?.pageThird && (
                <>
                  <h3 className="sub">Clinical impact and risk</h3>
                  <Prose text={analysis.pageThird} crux={crux} />
                </>
              )}
            </section>
          )}

          {(analysis?.pageFourth || archVisionAnalysis?.insoleRecommendation || archVisionAnalysis?.footwearAdvice) && (
            <section className="report-page orthotic-page"><p className="eyebrow">RECOMMENDATIONS</p>
              <Section title={titles.pageFourth} sub={bodyOnly ? 'Personalised exercise guidance' : 'Support, comfort and alignment'} />
              {archVisionAnalysis?.insoleRecommendation && <Callout label="Prescription summary" tone="good"><Prose text={archVisionAnalysis.insoleRecommendation} /></Callout>}
              {analysis?.pageFourth && <Prose text={analysis.pageFourth} crux={crux} />}
              {archVisionAnalysis?.footwearAdvice && <Callout label="Footwear" tone="info"><Prose text={archVisionAnalysis.footwearAdvice} /></Callout>}
            </section>
          )}
          {(analysis?.pageFifth || visionAnalysis?.recommendedAction) && (
            <section className="report-page rehabilitation-page"><p className="eyebrow">ACTION PLAN</p>
              <Section title={titles.pageFifth} sub="Lifestyle and follow-up" />
              {analysis?.pageFifth && <Prose text={analysis.pageFifth} crux={crux} />}
              {visionAnalysis?.recommendedAction && <Callout label="Recommended action" tone="good"><Prose text={visionAnalysis.recommendedAction} /></Callout>}
            </section>
          )}
          <section className="report-page record-page"><p className="eyebrow">CLINICAL RECORD</p>
          {/* ── Limitations ────────────────────────────────────────────── */}
          <Section title="Limitations" />
          <p>
            Measurements are made in static stance and characterise posture and load distribution at
            rest. They do not describe dynamic function through gait, and a rearfoot angle measured in
            relaxed calcaneal stance may differ from the same angle through midstance. Angles derived
            from a single photographic plane carry an out-of-plane component that cannot be resolved
            from that plane alone. This report should be read together with the clinical examination
            and not in isolation from it.
          </p>
          {captureQuality?.captures?.length > 0 && (
            <KeyValues
              rows={[
                ['Capture quality', `${captureQuality.worst === 'pass' ? 'Acceptable' : captureQuality.worst === 'warn' ? 'Acceptable with warnings' : 'Flagged'} · mean ${captureQuality.averageScore}/100 across ${captureQuality.captures.length} captures`],
                ['Flagged captures', captureQuality.failedCaptures?.length ? captureQuality.failedCaptures.join(', ') : 'None'],
              ]}
            />
          )}

          {/* ── Authentication ─────────────────────────────────────────── */}
          <Section title="Authentication" />
          <Authentication clinician={vendorName || 'KinetiQ Footcare'} date={scanDate} />

          {/* ── Method is intentionally last ──────────────────────────── */}
          <Section title="Method" />
          <p>
            Assessment performed barefoot in relaxed bilateral stance. Anatomical landmarks were
            located from calibrated photographs and reviewed by the assessing clinician before
            measurement. Angles are computed in the image plane and are independent of scale.{' '}
            {pressure.some(m => m.source === 'sensor')
              ? 'Plantar loading was recorded on a sensor platform.'
              : pressure.length > 0
                ? 'Podoscope images provide optical contact estimates, not measured pressure.'
                : ''}
          </p>

          <p className="disclaimer">
            This report describes a biomechanical assessment and does not constitute a medical
            diagnosis. It is issued to the user named above and to their referring clinician, and
            should be interpreted alongside the clinical findings. Reference ranges are those held in
            the assessment system at the date of report and are printed against each measurement.
            Report {id} · {scanDate}.
          </p>
          </section>
        </div>
      </div>
    </>
  );
}

/** The page: fetch the scan this link names, then hand it to the document. */
const Report = () => {
  const { id } = useParams();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'scans', id));
        if (cancelled) return;
        if (snap.exists()) setScan(snap.data());
        else toast.error('Report not found or invalid link.');
      } catch {
        if (!cancelled) toast.error('Failed to load report.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <Loading />;
  if (!scan) return <NotFound />;
  return <ReportDocument scan={scan} id={id} />;
};

export default Report;
